import { InventoryTarget } from '@cibo/core'
import { Loading } from '@cibo/profile'
import Check from '@mui/icons-material/Check'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { LoadingButton } from '@mui/lab'
import { Alert, Button, Container, Grid, Paper, Stack, Typography, useTheme } from '@mui/material'
import { AxiosError } from 'axios'
import { useInventory } from 'features/Inventories'
import { useInventoryTargets } from 'features/Inventories/queries/useInventoryTargets'
import { useSetInventoryTargets } from 'features/Inventories/queries/useSetInventoryTarget'
import { SCOPE_THREE_ROUTES } from 'navigation/types/scopeThreeRoutes'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { TargetEditor, TargetObject } from './TargetEditor'
import { useInventoryTargetStore } from './inventoryTargetStore'

export const InventoryTargets = () => {
  const { inventoryId } = useParams()
  const { data: inventory, isLoading, dataUpdatedAt: inventoryDataUpdatedAt } = useInventory(
    inventoryId
  )
  const { data: targets, isLoading: targetsLoading, dataUpdatedAt } = useInventoryTargets(
    inventoryId
  )
  const setInventoryTargets = useSetInventoryTargets(inventoryId)
  const [error, setError] = useState<AxiosError<any>>()
  const {
    absoluteEmissions,
    setAbsoluteEmissionsReductionPercent,
    setAbsoluteEmissionsYear,
    removals,
    setAbsoluteEmissionsTarget,
    clearAbsoluteEmissionsTarget,
    setIntensityTargets,
    createRemovalsTarget,
    removeRemovalsTarget,
    setRemovalsTonnes,
    setRemovalsYear,
    carbonIntensity,
    targetCrops,
    setTargetCrops,
    createEmptyIntensityTargets,
    removeIntensityTargets,
    setTargetIntensity,
    setTargetYear,
  } = useInventoryTargetStore()

  useEffect(() => {
    if (!targetsLoading && !isLoading) {
      if (targets && inventory) {
        const targetCrops = []
        !!inventory.item.data.ciJobResult?.ciReport?.find(a => a.corn) && targetCrops.push('corn')
        !!inventory.item.data.ciJobResult?.ciReport?.find(a => a.soybeans) &&
          targetCrops.push('soybeans')
        setTargetCrops(targetCrops as Array<'soybeans' | 'corn'>)
      }
      targets?.absoluteEmissions
        ? setAbsoluteEmissionsTarget(targets?.absoluteEmissions)
        : clearAbsoluteEmissionsTarget()
      targets?.carbonIntensity
        ? setIntensityTargets(targets?.carbonIntensity)
        : removeIntensityTargets()
      targets?.removals ? createRemovalsTarget(targets?.removals) : removeRemovalsTarget()
    }
  }, [dataUpdatedAt, inventoryDataUpdatedAt])

  const { t } = useTranslation('impact/InventoryTargets')
  const { t: cropsT } = useTranslation('@cibo/ui/cropNames')
  const navigate = useNavigate()
  const { palette } = useTheme()

  const baselineTonnes = targets?.totalCo2EmissionsBaseLine
  const cropIntensity: Record<'corn' | 'soybeans', number | undefined> = {
    corn: targets?.carbonIntensities && targets?.carbonIntensities.currentCorn,
    soybeans: targets?.carbonIntensities && targets?.carbonIntensities.currentSoybeans,
  }

  if (isLoading) return <Loading large />

  if (!!inventory) {
    return (
      <Container>
        <Grid container spacing={3} paddingBottom={3}>
          <Grid item sm={12} md={4}>
            <Paper variant="outlined" sx={{ borderRadius: '12px' }}>
              <Grid container padding={2} spacing={2}>
                <Grid item style={{ maxWidth: '40px' }}>
                  <HelpOutlineIcon htmlColor={palette.text.secondary} />
                </Grid>
                <Grid item style={{ maxWidth: 'calc(100% - 56px)' }}>
                  <Stack spacing={2}>
                    <Typography variant="h6">{t('setupTip')}</Typography>
                    <Typography>{t('setupTipContent')}</Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item sm={12} md={8}>
            <Stack spacing={3}>
              <TargetEditor
                title={t('absoluteEmissions')}
                inputAdornment={{
                  start: t('reduceBy'),
                  end: '%',
                }}
                hasTarget
                year={{
                  value: absoluteEmissions.year,
                  setValue: setAbsoluteEmissionsYear,
                }}
                targets={[
                  {
                    name: t('total'),
                    value: absoluteEmissions.reducePer,
                    default: 20,
                    min: 0,
                    max: 100,
                    increment: 1,
                    setValue: setAbsoluteEmissionsReductionPercent,
                    baseline: t('totalEmissionsBaseline', {
                      baselineTonnes,
                    }),
                  },
                ]}
              />
              <TargetEditor
                title={t('removals')}
                optional
                inputAdornment={{
                  start: t('remove'),
                }}
                hasTarget={!!removals}
                createTarget={createRemovalsTarget}
                removeTarget={removeRemovalsTarget}
                year={{
                  value: removals?.year,
                  setValue: setRemovalsYear,
                }}
                targets={[
                  {
                    name: t('total'),
                    value: removals?.removeTonnes,
                    default: 10,
                    min: 0,
                    max: baselineTonnes || 1000000000,
                    increment: 1,
                    setValue: setRemovalsTonnes,
                    baseline: t('totalEmissionsBaseline', {
                      baselineTonnes,
                    }),
                  },
                ]}
              />
              <TargetEditor
                title={t('carbonIntensity')}
                optional
                inputAdornment={{
                  start: t('reduceTo'),
                }}
                hasTarget={!!carbonIntensity}
                createTarget={createEmptyIntensityTargets}
                removeTarget={removeIntensityTargets}
                year={{
                  value: !!carbonIntensity?.[targetCrops[0]]
                    ? carbonIntensity[targetCrops[0]].year
                    : '',
                  setValue: setTargetYear,
                }}
                targets={
                  targetCrops
                    ?.map(crop => {
                      return !Number.isNaN(cropIntensity[crop])
                        ? {
                            name: cropsT(crop),
                            value: carbonIntensity?.[crop]?.reduceTo,
                            default: (cropIntensity[crop] as number) - 0.05 || 0.0,
                            min: 0,
                            max: cropIntensity[crop] || 0.5,
                            increment: 0.01,
                            setValue: (val: number) => setTargetIntensity(crop, val),
                            baseline: t('offtakeBaseline', {
                              cropOfftake: cropIntensity[crop] || t('unknown'),
                            }),
                          }
                        : undefined
                    })
                    .filter(Boolean) as Array<TargetObject>
                }
              />
              {error && (
                <Alert severity="error">
                  {error.response?.data.message || error.message || t('errorCreatingTargets')}
                </Alert>
              )}
              <Stack direction="row" spacing={3} alignSelf="flex-end">
                <Button onClick={() => navigate(-1)} color="secondary">
                  {t('cancel')}
                </Button>
                <LoadingButton
                  disabled={
                    !absoluteEmissions.year ||
                    (!!removals && !removals.year) ||
                    (!!carbonIntensity && !Object.values(carbonIntensity).every(a => a.year))
                  }
                  startIcon={<Check />}
                  variant="contained"
                  onClick={() =>
                    setInventoryTargets.mutateAsync(
                      {
                        absoluteEmissions,
                        removals,
                        carbonIntensity: carbonIntensity && Object.values(carbonIntensity),
                      } as InventoryTarget,
                      {
                        onSuccess: () =>
                          navigate(generatePath(SCOPE_THREE_ROUTES.DETAIL, { inventoryId })),
                        onError: error => setError(error),
                      }
                    )
                  }
                >
                  {t('saveTargets')}
                </LoadingButton>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    )
  }
  return (
    <Container>
      <Alert severity="error">{t('missingInventory')}</Alert>
    </Container>
  )
}
